/**
 * YouTube Data API v3 search. Uses search.list (100 units) + videos.list for
 * statistics (1 unit). Cached with 5min staleTime to reduce quota usage.
 */
import {useInfiniteQuery} from '@tanstack/react-query'

import {YOUTUBE_API_KEY} from '#/env/common'
import {STALE} from '#/state/queries'

const YOUTUBE_SEARCH_ROOT = 'youtube-search'
const PAGE_SIZE = 15
const MAX_PAGES = 2

/** User-facing error for YouTube API failures (quota, auth, etc.). */
function youtubeApiError(status: number, bodyText: string): Error {
  if (status === 403) {
    try {
      const body = JSON.parse(bodyText) as {
        error?: { errors?: Array<{ reason?: string; domain?: string }> }
      }
      const reason = body?.error?.errors?.[0]?.reason
      if (reason === 'quotaExceeded' || bodyText.includes('exceeded your quota')) {
        return new Error(
          'YouTube API daily quota exceeded. Quota resets at midnight Pacific Time. Try again later or request a quota increase in Google Cloud Console.',
        )
      }
    } catch {
      /* ignore parse errors */
    }
  }
  return new Error(
    bodyText && bodyText.length < 200 ? bodyText : `YouTube search failed (${status}). Try again later.`,
  )
}

export type YouTubeSearchItem = {
  id: string
  title: string
  description: string
  channelTitle: string
  channelId: string
  publishedAt: string
  thumbnail: string
  viewCount?: string
  likeCount?: string
}

function searchYouTubeSearchKey(query: string) {
  return [YOUTUBE_SEARCH_ROOT, query] as const
}

async function fetchYouTubeSearchPage(
  query: string,
  pageToken?: string,
): Promise<{
  items: YouTubeSearchItem[]
  nextPageToken?: string
}> {
  if (!YOUTUBE_API_KEY) {
    throw new Error('EXPO_PUBLIC_YOUTUBE_API_KEY is not set')
  }

  const searchRes = await fetch(
    'https://www.googleapis.com/youtube/v3/search?' +
      new URLSearchParams({
        key: YOUTUBE_API_KEY,
        part: 'snippet',
        type: 'video',
        videoDuration: 'medium',
        q: query,
        maxResults: String(PAGE_SIZE),
        safeSearch: 'moderate',
        ...(pageToken ? {pageToken} : {}),
      }),
  )
  if (!searchRes.ok) {
    const errText = await searchRes.text()
    throw youtubeApiError(searchRes.status, errText)
  }
  const searchData = await searchRes.json()
  const videoIds = (searchData.items || [])
    .map((i: {id?: {videoId?: string}}) => i.id?.videoId)
    .filter(Boolean)
  if (videoIds.length === 0) {
    return {
      items: [],
      nextPageToken: searchData.nextPageToken,
    }
  }

  const statsRes = await fetch(
    'https://www.googleapis.com/youtube/v3/videos?' +
      new URLSearchParams({
        key: YOUTUBE_API_KEY,
        part: 'statistics,snippet',
        id: videoIds.join(','),
      }),
  )
  if (!statsRes.ok) {
    return {
      items: (searchData.items || []).map(
        (i: {
          id?: {videoId?: string}
          snippet?: {
            title?: string
            description?: string
            channelTitle?: string
            channelId?: string
            publishedAt?: string
            thumbnails?: {medium?: {url?: string}; high?: {url?: string}}
          }
        }) => ({
          id: i.id?.videoId ?? '',
          title: i.snippet?.title ?? '',
          description: i.snippet?.description ?? '',
          channelTitle: i.snippet?.channelTitle ?? '',
          channelId: i.snippet?.channelId ?? '',
          publishedAt: i.snippet?.publishedAt ?? '',
          thumbnail:
            i.snippet?.thumbnails?.high?.url ??
            i.snippet?.thumbnails?.medium?.url ??
            '',
        }),
      ),
      nextPageToken: searchData.nextPageToken,
    }
  }
  const statsData = await statsRes.json()
  const statsMap = new Map(
    (statsData.items || []).map((v: {id: string; statistics?: Record<string, string>}) => [
      v.id,
      v.statistics ?? {},
    ]),
  )

  const items: YouTubeSearchItem[] = (searchData.items || []).map(
    (i: {
      id?: {videoId?: string}
      snippet?: {
        title?: string
        description?: string
        channelTitle?: string
        channelId?: string
        publishedAt?: string
        thumbnails?: {medium?: {url?: string}; high?: {url?: string}}
      }
    }) => {
      const id = i.id?.videoId ?? ''
      const stats = statsMap.get(id) ?? {}
      return {
        id,
        title: i.snippet?.title ?? '',
        description: i.snippet?.description ?? '',
        channelTitle: i.snippet?.channelTitle ?? '',
        channelId: i.snippet?.channelId ?? '',
        publishedAt: i.snippet?.publishedAt ?? '',
        thumbnail:
          i.snippet?.thumbnails?.high?.url ??
          i.snippet?.thumbnails?.medium?.url ??
          '',
        viewCount: stats.viewCount,
        likeCount: stats.likeCount,
      }
    },
  )

  return {
    items,
    nextPageToken: searchData.nextPageToken,
  }
}

export function useYouTubeSearchQuery({
  query,
  enabled,
}: {
  query: string
  enabled: boolean
}) {
  return useInfiniteQuery({
    queryKey: searchYouTubeSearchKey(query),
    queryFn: async ({pageParam}) => {
      return fetchYouTubeSearchPage(query, pageParam)
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage, allPages) => {
      if (allPages.length >= MAX_PAGES || !lastPage.nextPageToken) return undefined
      return lastPage.nextPageToken
    },
    staleTime: STALE.MINUTES.FIVE,
    enabled: enabled && !!query.trim() && !!YOUTUBE_API_KEY,
  })
}

export function isYouTubeSearchEnabled() {
  return !!YOUTUBE_API_KEY
}

const YOUTUBE_SHORTS_ROOT = 'youtube-shorts'
const SHORTS_PAGE_SIZE = 20

/** Fetch one page of YouTube Shorts (news only for now). videoDuration=short. */
async function fetchYouTubeShortsPage(
  pageToken?: string,
): Promise<{items: YouTubeSearchItem[]; nextPageToken?: string}> {
  if (!YOUTUBE_API_KEY) return {items: []}
  const searchRes = await fetch(
    'https://www.googleapis.com/youtube/v3/search?' +
      new URLSearchParams({
        key: YOUTUBE_API_KEY,
        part: 'snippet',
        type: 'video',
        videoDuration: 'short',
        q: 'news',
        maxResults: String(SHORTS_PAGE_SIZE),
        safeSearch: 'moderate',
        ...(pageToken ? {pageToken} : {}),
      }),
  )
  if (!searchRes.ok) return {items: []}
  const searchData = await searchRes.json()
  const videoIds = (searchData.items || [])
    .map((i: {id?: {videoId?: string}}) => i.id?.videoId)
    .filter(Boolean)
  if (videoIds.length === 0) {
    return {items: [], nextPageToken: searchData.nextPageToken}
  }
  const statsRes = await fetch(
    'https://www.googleapis.com/youtube/v3/videos?' +
      new URLSearchParams({
        key: YOUTUBE_API_KEY,
        part: 'statistics,snippet',
        id: videoIds.join(','),
      }),
  )
  if (!statsRes.ok) {
    return {
      items: (searchData.items || []).map(
        (i: {
          id?: {videoId?: string}
          snippet?: {
            title?: string
            description?: string
            channelTitle?: string
            channelId?: string
            publishedAt?: string
            thumbnails?: {medium?: {url?: string}; high?: {url?: string}}
          }
        }) => ({
          id: i.id?.videoId ?? '',
          title: i.snippet?.title ?? '',
          description: i.snippet?.description ?? '',
          channelTitle: i.snippet?.channelTitle ?? '',
          channelId: i.snippet?.channelId ?? '',
          publishedAt: i.snippet?.publishedAt ?? '',
          thumbnail:
            i.snippet?.thumbnails?.high?.url ??
            i.snippet?.thumbnails?.medium?.url ??
            '',
        }),
      ),
      nextPageToken: searchData.nextPageToken,
    }
  }
  const statsData = await statsRes.json()
  const statsMap = new Map(
    (statsData.items || []).map((v: {id: string; statistics?: Record<string, string>}) => [
      v.id,
      v.statistics ?? {},
    ]),
  )
  const items: YouTubeSearchItem[] = (searchData.items || []).map(
    (i: {
      id?: {videoId?: string}
      snippet?: {
        title?: string
        description?: string
        channelTitle?: string
        channelId?: string
        publishedAt?: string
        thumbnails?: {medium?: {url?: string}; high?: {url?: string}}
      }
    }) => {
      const id = i.id?.videoId ?? ''
      const stats = statsMap.get(id) ?? {}
      return {
        id,
        title: i.snippet?.title ?? '',
        description: i.snippet?.description ?? '',
        channelTitle: i.snippet?.channelTitle ?? '',
        channelId: i.snippet?.channelId ?? '',
        publishedAt: i.snippet?.publishedAt ?? '',
        thumbnail:
          i.snippet?.thumbnails?.high?.url ??
          i.snippet?.thumbnails?.medium?.url ??
          '',
        viewCount: stats.viewCount,
        likeCount: stats.likeCount,
      }
    },
  )
  return {items, nextPageToken: searchData.nextPageToken}
}

export function useYouTubeShortsQuery(opts?: {enabled?: boolean}) {
  return useInfiniteQuery({
    queryKey: [YOUTUBE_SHORTS_ROOT, 'news'],
    queryFn: ({pageParam}) => fetchYouTubeShortsPage(pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: last => last.nextPageToken,
    staleTime: STALE.MINUTES.FIVE,
    enabled: (opts?.enabled ?? false) && !!YOUTUBE_API_KEY,
  })
}
